import awsCognito, {
  FEDERATION_PROVIDERS,
} from "../../../../app/security/awsCognito";

import { DivWithSpinner } from "@tedivo/tedivo-ui";
import { FederationProvidersEnum } from "@tedivo/tvd-api-models";
import { I18nComponentRegisterer } from "@tedivo/tedivo-i18";
import Services from "../../../../app/services";
import { SlInput } from "@shoelace-style/shoelace";
import { entraIdFederationProvider } from "../../../../app/security/entraIdFederationProvider";
import { getTranslation } from "../../../../app/i18/i18tn";
import globalStore from "../../../../app/stores/globalStore";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";
import { routeFns } from "../../../../app/router/routes";
import { z } from "zod";

export class MultiStepLogin {
  private nodes: {
    part1: HTMLDivElement;
    part2: HTMLDivElement;
    holder: DivWithSpinner;
    formNodePart1: HTMLFormElement;
    formNodePart2?: HTMLFormElement;
    inputEmail: SlInput;
    inputPassword?: SlInput;
  };

  private _emailHasFocus = false;
  private _id = Math.random().toString(36).substring(2, 9);

  data: {
    email?: string;
    password?: string;
  } = {};

  private part1HasBeenSubmitted = false;
  private part2HasBeenSubmitted = false;

  get holder() {
    return this.nodes.holder;
  }

  get emailHasFocus() {
    return this._emailHasFocus;
  }
  set emailHasFocus(value: boolean) {
    this._emailHasFocus = value;
  }

  constructor(
    private i18nCR: I18nComponentRegisterer,
    private state: IViewStateLogin,
    private queryString: IViewStateLogin,
    private onSubmitLoginForm: (values: {
      username: string;
      password: string;
    }) => void,
  ) {
    this.nodes = this.buildHtml();
    this.attachInitialEvents();

    if (state?.email && state?.password && state?.fromVerification) {
      console.log("From verification", state);
      this.data.email = state.email;
      this.data.password = state.password;
      this.onSubmitLoginForm({
        username: state.email,
        password: state.password,
      });
      state.email = undefined;
      state.password = undefined;
      state.fromVerification = undefined;
    } else if (queryString.email && queryString.password) {
      this.data.email = queryString.email;
      this.data.password = queryString.password;
      this.onSubmitLoginForm({
        username: queryString.email,
        password: queryString.password,
      });
    } else {
      this.showStep(LoginSteps.EmailInput);
    }
  }

  private updateData = (key: "email" | "password", value: string) => {
    this.data[key] = value;
  };

  private buildHtml() {
    const i18nCR = this.i18nCR;
    const state = this.state;
    const queryString = this.queryString;

    const holder = document.createElement("div-spinner-element");
    holder.className = "login-form-part";
    holder.centered = true;

    const part1 = document.createElement("div");
    const part2 = document.createElement("div");

    // 1. Part 1, only email
    const { inputEmail, formNodePart1 } = _createPartEmail(part1);
    // 2. Don't create the part 2, it's dynamic

    inputEmail.addEventListener("sl-focus", () => {
      this.emailHasFocus = true;
    });
    inputEmail.addEventListener("sl-blur", () => {
      this.emailHasFocus = false;
    });

    holder.appendChild(part1);
    holder.appendChild(part2);

    return {
      holder,
      part1,
      part2,
      inputEmail,
      formNodePart1,
    };

    function _createPartEmail(partHolder: HTMLDivElement) {
      removeChildren(partHolder);

      const { headerNode, footerNode } = createLoginHeaderAndFooter(
        i18nCR,
        undefined,
        undefined,
      );

      const formNodePart1 = document.createElement("form");

      const divInputs = document.createElement("div");
      divInputs.className = "form-fields-row ";

      const inputEmail = document.createElement("sl-input");
      inputEmail.type = "email";
      inputEmail.autocomplete = "username";
      inputEmail.value = state?.email || queryString?.email || "";
      i18nCR.addConsumer(
        inputEmail as unknown as HTMLElement,
        "auth:email",
        "label",
      );

      divInputs.appendChild(inputEmail);

      const continueButton = document.createElement("sl-button");
      i18nCR.addConsumer(
        continueButton,
        "general:common.continue",
        "innerHTML",
      );
      continueButton.variant = "primary";
      continueButton.type = "submit";

      footerNode.insertBefore(continueButton, footerNode.firstChild);

      formNodePart1.appendChild(headerNode);
      formNodePart1.appendChild(divInputs);
      formNodePart1.appendChild(footerNode);

      partHolder.appendChild(formNodePart1);

      return { inputEmail, continueButton, formNodePart1 };
    }
  }

  private attachInitialEvents() {
    const inputEmail = this.nodes.inputEmail;
    const formNodePart1 = this.nodes.formNodePart1;

    const validateEmail = () => {
      const result = z
        .object({
          username: z.string().email(),
        })
        .safeParse({
          username: inputEmail.value,
        });

      if (!result.success) {
        console.log("invalid email");
        inputEmail.classList.add("has-error");
      } else {
        inputEmail.classList.remove("has-error");
      }

      return result;
    };

    formNodePart1.onsubmit = (ev: SubmitEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      ev.stopImmediatePropagation();

      this.part1HasBeenSubmitted = true;

      const result = validateEmail();
      console.log("Valid email", result);

      if (result.success) {
        this.updateData("email", result.data.username);
        this.showStep(LoginSteps.PasswordOrFederation);
      }

      return false;
    };

    inputEmail.addEventListener("sl-change", () => {
      if (!this.part1HasBeenSubmitted) return;
      validateEmail();
    });
  }

  private attachSecondaryEvents(
    inputPassword: SlInput | undefined,
    formNodePart2: HTMLFormElement | undefined,
  ) {
    if (!inputPassword || !formNodePart2) return;

    const validatePassword = () => {
      const result = z
        .object({
          password: z.string().min(1),
        })
        .safeParse({
          password: inputPassword.value,
        });

      if (!result.success) {
        inputPassword.classList.add("has-error");
      } else {
        inputPassword.classList.remove("has-error");
        this.onSubmitLoginForm({
          username: this.data.email || "",
          password: result.data.password,
        });
      }

      return result;
    };

    formNodePart2.onsubmit = (ev: SubmitEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      ev.stopImmediatePropagation();

      this.part2HasBeenSubmitted = true;

      const result = validatePassword();

      if (result.success) {
        this.updateData("password", result.data.password);
        // TODO: DO THE LOGIN
      }

      return false;
    };

    inputPassword.addEventListener("sl-change", () => {
      if (!this.part2HasBeenSubmitted) return;
      validatePassword();
    });
  }

  showStep = async (step: LoginSteps) => {
    switch (step) {
      case LoginSteps.EmailInput:
        this.nodes.part1.style.display = "block";
        this.nodes.part2.style.display = "none";
        this.holder.setLoading(false);
        this.focusOnEmail();
        break;

      case LoginSteps.PasswordOrFederation: {
        if (!this.data.email) {
          this.showStep(LoginSteps.EmailInput);
          return;
        }

        this.holder.setLoading(true);
        const resp = await Services.accounts.getLoginOptions(this.data.email);

        if (!resp || (resp.statusCode !== 200 && resp.statusCode !== 204)) {
          this.showStep(LoginSteps.EmailInput);
          return;
        }

        const federationProvider = (
          resp.statusCode === 200 ? resp.data?.provider : undefined
        ) as FederationProvidersEnum | undefined;

        //const federationProvider = undefined;

        this.createPartPassOrFederation({
          partHolder: this.nodes.part2,
          federationProvider,
          returnToStep1: () => this.showStep(LoginSteps.EmailInput),
        });

        this.nodes.part1.style.display = "none";
        this.nodes.part2.style.display = "block";
        this.holder.setLoading(false);

        break;
      }

      default:
        console.error("Unknown step:", step);
        break;
    }
  };

  createPartPassOrFederation = ({
    partHolder,
    federationProvider,
    returnToStep1,
  }: {
    partHolder: HTMLDivElement;
    federationProvider: FederationProvidersEnum | undefined;
    returnToStep1: () => void;
  }) => {
    removeChildren(partHolder);
    const contentNode = document.createElement("div");

    const { headerNode, footerNode } = createLoginHeaderAndFooter(
      this.i18nCR,
      this.data.email,
      returnToStep1,
    );

    if (!federationProvider) {
      const formNodePart2 = document.createElement("form");

      const inputPassword = document.createElement("sl-input");
      inputPassword.type = "password";
      inputPassword.passwordToggle = true;
      inputPassword.autocomplete = "current-password";
      inputPassword.value =
        this.state?.password || this.queryString?.password || "";
      this.i18nCR.addConsumer(
        inputPassword as unknown as HTMLElement,
        "auth:password",
        "label",
      );

      if (this.queryString?.hint)
        inputPassword.placeholder = getTranslation("auth:passwordFromEmail");

      contentNode.appendChild(inputPassword);

      const submitButton = document.createElement("sl-button");
      this.i18nCR.addConsumer(
        submitButton,
        "general:common.submit",
        "innerHTML",
      );
      submitButton.variant = "primary";
      submitButton.type = "submit";

      footerNode.insertBefore(submitButton, footerNode.firstChild);

      formNodePart2.appendChild(headerNode);
      formNodePart2.appendChild(contentNode);
      formNodePart2.appendChild(footerNode);

      partHolder.appendChild(formNodePart2);

      this.attachSecondaryEvents(inputPassword, formNodePart2);
    } else {
      const federationButtons = document.createElement("div");
      federationButtons.className = "federation-buttons";

      const img = document.createElement("img");
      img.src = `assets/images/fedprovider_${FEDERATION_PROVIDERS[federationProvider]}_${globalStore.resolvedColorMode}.svg`;
      img.role = "button";
      img.tabIndex = 0;

      const federationButton = document.createElement("sl-button");
      federationButton.variant = "primary";
      federationButton.outline = true;
      federationButton.type = "button";

      federationButton.appendChild(img);

      this.i18nCR.addConsumer(
        img,
        `auth:providers.${federationProvider}`,
        "title",
      );

      img.addEventListener("click", async () => {
        img.style.opacity = "0.5";
        img.style.pointerEvents = "none";
        img.style.cursor = "wait";

        if (federationProvider === FederationProvidersEnum.Microsoft) {
          this.holder.setLoading(true);
          const res = await entraIdFederationProvider.loginWithPopup(
            this.data.email,
          );

          if (res) {
            await awsCognito
              .authenticateUserWithCode(res.email, res.customAuthCode)
              .finally(() => this.holder.setLoading(false));
          } else {
            this.holder.setLoading(false);
          }
        } else {
          const url =
            awsCognito.getRedirectFederationLoginUrl(federationProvider);

          if (url) window.location.href = url;
        }
      });

      federationButtons.appendChild(img);
      contentNode.appendChild(federationButtons);

      partHolder.appendChild(headerNode);
      partHolder.appendChild(federationButtons);
    }
  };

  focusOnEmail = () => {
    if (this.emailHasFocus) return;

    try {
      if (this.nodes?.inputEmail?.parentNode) this.nodes?.inputEmail?.focus();
    } catch (error) {
      // Nothing
    }
  };

  focusOnPassword = () => {
    try {
      if (this.nodes?.inputPassword?.parentNode)
        this.nodes?.inputPassword?.focus();
    } catch (error) {
      // console.error("Error focusing on email input:", error);
    }
  };
}

function createLoginHeaderAndFooter(
  i18nCR: I18nComponentRegisterer,
  email: string | undefined,
  returnToStep1: (() => void) | undefined,
) {
  const headerNode = document.createElement("header");
  const footerNode = document.createElement("footer");
  footerNode.className = "login-button-container";

  const h2 = document.createElement("h2");
  i18nCR.addConsumer(h2, "auth:logIn", "innerHTML");

  const h3 = document.createElement("h3");

  if (email) {
    const btnChangeUser = document.createElement("sl-icon-button");
    btnChangeUser.setAttribute("name", "arrow-left-right");
    btnChangeUser.title = getTranslation("auth:switchAccount");
    btnChangeUser.addEventListener("click", () => {
      returnToStep1?.();
    });

    i18nCR.addConsumer(h3, "auth:emailAccount", "innerHTML", { email });

    h3.appendChild(btnChangeUser);
  } else {
    i18nCR.addConsumer(h3, "general:welcomeComp.subTitleLogin", "innerHTML");
  }

  headerNode.appendChild(h2);
  headerNode.appendChild(h3);

  // Below the form, we add a link to the forgot password page
  const forgotPasswordLink = document.createElement("link-element");
  i18nCR.addConsumer(forgotPasswordLink, "auth:forgotPassword", "text");

  forgotPasswordLink.setAttribute("url", routeFns.forgotPassword());

  const orSeparator = document.createElement("span");
  orSeparator.className = "text-with-separation";
  i18nCR.addConsumer(orSeparator, "general:common.or", "innerHTML");

  footerNode.appendChild(orSeparator);
  footerNode.appendChild(forgotPasswordLink);

  return {
    headerNode,
    footerNode,
  };
}

export enum LoginSteps {
  EmailInput = 1,
  PasswordOrFederation = 2,
}

interface IViewStateLogin {
  email?: string;
  password?: string;
  fromVerification?: boolean;
  returnUrl?: string;
  hint?: string;
}
